/**
 * Tribe definitions — single source of truth for all 6 sub-tribes.
 * Each tribe belongs to a parent faction (emo/cholo) and has
 * tiered synergy breakpoints at 2/4/6 units.
 *
 * Colors mirror Colors.tribes.* in UIConstants.ts. Keep them in sync —
 * CardPortrait and TavernBoardZone read from tribeDef.color, not from
 * the Colors token, so both must move together when the palette changes.
 *
 * Cultural framing per docs/card-rename-mapping.md and docs/visual-conventions.md.
 */

import { TribeId } from './cards/types';

/**
 * The one-word verb a tribe hands the player. Surfaced in the tavern UI so
 * the contract is legible at draft time, not discovered three rounds late.
 */
export type TribeVerb = 'CODAZO' | 'SETLIST' | 'SEGUNDO AIRE' | 'PLACA' | 'RAZA' | 'BRINCO';

export type TribeFlagName =
  | 'firstStrike'
  | 'reborn'
  | 'divineShield'
  | 'overkill'
  | 'cleave'
  | 'windfury';

export interface TribeSynergyBreakpoint {
  threshold: number;
  description: string;
  /** Stat buffs applied to tribe members (or all friendlies, per scope) */
  statBuff?: { attack: number; health: number };
  /** If true, buffs apply to ALL friendly units, not just tribe members */
  buffAllFriendlies?: boolean;
  /** Flags to grant to specific units */
  grantFlags?: {
    target: 'leftmost' | 'strongest' | 'all';
    flags: Partial<Record<TribeFlagName, boolean>>;
  };
  /** Direct damage to random enemies at start of combat */
  damageRandomEnemy?: { damage: number; count: number };
  /** Heal all friendlies at start of combat */
  healAllFriendlies?: number;

  // --- Tavern phase (fires in match.ts endTurn, before the board is sent to combat) ---

  /** La Clica: summon a Morrito with these stats at end of turn */
  summonToken?: { attack: number; health: number };
  /** La Clica: permanently buff every token already on the board at end of turn */
  tokenBuff?: { attack: number; health: number };
  /**
   * Plaqueros: apply Plaque tags at end of turn. Each tag is +1/+1 permanent
   * plus a plaqueStacks increment, so tags compound with plaqueCashIn below —
   * stacks grow linearly per round and the cash-in scales off the count,
   * which is what makes the Snowball curve superlinear.
   */
  plaqueTag?: {
    /** Tags applied to the primary carry (highest Attack tribe member) */
    count: number;
    /** Tags applied to every OTHER tribe member (0 = carry only) */
    othersCount?: number;
  };

  // --- Combat phase (consumed by combatEngine via TribeCombatHooks) ---

  /** Bajitos: how the hop rewrites stats at start of combat */
  statManipulation?: 'boost_higher' | 'swap_if_beneficial' | 'set_attack_to_health';
  /** Extra flat stats applied after the hop */
  manipulationBonus?: { attack: number; health: number };
  /** La Tocada 6: multiply every friendly unit's Attack at start of combat */
  attackMultiplier?: number;
  /** Plaqueros 6: each tribe member gains this per Plaque tag it carries */
  plaqueCashIn?: { attackPer: number; healthPer: number };
  /** Anclas 6: Reborn units return at full Health with a bonus instead of at 1 HP */
  rebornUpgrade?: { fullHealth: boolean; bonus: { attack: number; health: number } };
  /**
   * La Clica: summon a Morrito mid-combat when a unit dies. Every Morrito
   * summoned this combat also applies swarmBuff to all friendly tokens —
   * the swarm gets stronger the longer the fight runs.
   */
  deathSummon?: {
    scope: 'tribe' | 'any_friendly';
    token: { attack: number; health: number };
    swarmBuff?: { attack: number; health: number };
  };
}

export interface TribeDefinition {
  id: TribeId;
  name: string;
  faction: 'emo' | 'cholo';
  color: number;
  /** The tribe's power-fantasy verb — one word, shown on the synergy pip. */
  verb: TribeVerb;
  /** One line of "what this tribe lets you do", in player language. */
  fantasy: string;
  description: string;
  breakpoints: TribeSynergyBreakpoint[];
}

export const TRIBE_DEFINITIONS: TribeDefinition[] = [
  // === EMO SUB-TRIBES ===
  {
    id: 'slameros',
    name: 'Slameros',
    faction: 'emo',
    color: 0xe6342a, // aerosol red (mosh-pit blood)
    verb: 'CODAZO',
    fantasy: 'One swing clears the row. You hit first and you hit through.',
    description: 'Pit-kid aggression. Stack attack and throw the first elbow.',
    breakpoints: [
      {
        threshold: 2,
        description: 'All Slameros gain +2 Attack.',
        statBuff: { attack: 2, health: 0 },
      },
      {
        threshold: 4,
        description: 'All Slameros gain +4 Attack and First Strike.',
        statBuff: { attack: 4, health: 0 },
        grantFlags: { target: 'all', flags: { firstStrike: true } },
      },
      {
        threshold: 6,
        description:
          'All Slameros gain +8 Attack, First Strike and Overkill — excess damage spills to the next enemy. Deal 4 damage to two random enemies at combat start.',
        statBuff: { attack: 8, health: 0 },
        grantFlags: { target: 'all', flags: { firstStrike: true, overkill: true } },
        damageRandomEnemy: { damage: 4, count: 2 },
      },
    ],
  },
  {
    id: 'la_tocada',
    name: 'La Tocada',
    faction: 'emo',
    color: 0xff2e7e, // stage-light neon hot-pink
    verb: 'SETLIST',
    fantasy: 'The whole band levels up. Your worst unit becomes a threat.',
    description: 'The gig lifts the crew. Auras that amplify your whole board.',
    breakpoints: [
      {
        threshold: 2,
        description: 'All La Tocada gain +2/+2.',
        statBuff: { attack: 2, health: 2 },
      },
      {
        threshold: 4,
        description: 'All friendly units gain +3/+3.',
        statBuff: { attack: 3, health: 3 },
        buffAllFriendlies: true,
      },
      {
        threshold: 6,
        description:
          'All friendly units gain +3/+3, then DOUBLE their Attack at combat start.',
        statBuff: { attack: 3, health: 3 },
        buffAllFriendlies: true,
        attackMultiplier: 2,
      },
    ],
  },
  {
    id: 'anclas',
    name: 'Anclas del Backyard',
    faction: 'emo',
    color: 0x5fd17e, // backyard-string-light green
    verb: 'SEGUNDO AIRE',
    fantasy: "They kill you and you stand back up bigger. Nobody closes the fight.",
    description: 'The aunties who keep the scene alive. Outlast everyone.',
    breakpoints: [
      {
        threshold: 2,
        description: 'All Anclas gain +4 Health.',
        statBuff: { attack: 0, health: 4 },
      },
      {
        threshold: 4,
        description: 'All Anclas gain +6 Health and Reborn.',
        statBuff: { attack: 0, health: 6 },
        grantFlags: { target: 'all', flags: { reborn: true } },
      },
      {
        threshold: 6,
        description:
          'All Anclas gain +10 Health, Reborn, and Divine Shield. Reborn units return at FULL Health with +4/+4. All friendlies heal 5 at combat start.',
        statBuff: { attack: 0, health: 10 },
        grantFlags: { target: 'all', flags: { reborn: true, divineShield: true } },
        healAllFriendlies: 5,
        rebornUpgrade: { fullHealth: true, bonus: { attack: 4, health: 4 } },
      },
    ],
  },

  // === CHOLO SUB-TRIBES ===
  {
    id: 'plaqueros',
    name: 'Plaqueros',
    faction: 'cholo',
    color: 0xc0c4ca, // spray-can chrome
    verb: 'PLACA',
    fantasy: 'One unit becomes the biggest thing in the lobby. Everyone knows its name.',
    description: 'Taggers leaving a permanent placa. The piece grows over the night.',
    breakpoints: [
      {
        threshold: 2,
        description:
          'End of turn: Tag your highest-Attack Plaquero — it gains +2/+2 permanently.',
        plaqueTag: { count: 2 },
      },
      {
        threshold: 4,
        description:
          'End of turn: Tag your highest-Attack Plaquero 4 times (+4/+4) and every other Plaquero once.',
        plaqueTag: { count: 4, othersCount: 1 },
      },
      {
        threshold: 6,
        description:
          'End of turn: Tag your highest-Attack Plaquero 6 times and every other Plaquero twice. Start of combat: every Plaquero gains +2/+2 per tag it carries.',
        plaqueTag: { count: 6, othersCount: 2 },
        plaqueCashIn: { attackPer: 2, healthPer: 2 },
      },
    ],
  },
  {
    id: 'la_clica',
    name: 'La Clica',
    faction: 'cholo',
    color: 0xff8a3d, // sodium-lamp orange (barrio palette)
    verb: 'RAZA',
    fantasy: 'The block refills faster than they can clear it, and each wave is bigger.',
    description: 'Rolling deep with the crew. Swarm the block with tokens.',
    breakpoints: [
      {
        threshold: 2,
        description: 'End of turn: Summon a 2/2 Morrito. All tokens gain +1/+1.',
        summonToken: { attack: 2, health: 2 },
        tokenBuff: { attack: 1, health: 1 },
      },
      {
        threshold: 4,
        description:
          'End of turn: Summon a 4/4 Morrito. All tokens gain +2/+2. When a La Clica unit dies in combat, summon a 4/4 Morrito.',
        summonToken: { attack: 4, health: 4 },
        tokenBuff: { attack: 2, health: 2 },
        deathSummon: { scope: 'tribe', token: { attack: 4, health: 4 } },
      },
      {
        threshold: 6,
        description:
          'End of turn: Summon a 6/6 Morrito. All tokens gain +3/+3. When ANY friendly dies in combat, summon a 6/6 Morrito — and every Morrito summoned gives all your tokens +2/+2.',
        summonToken: { attack: 6, health: 6 },
        tokenBuff: { attack: 3, health: 3 },
        deathSummon: {
          scope: 'any_friendly',
          token: { attack: 6, health: 6 },
          swarmBuff: { attack: 2, health: 2 },
        },
      },
    ],
  },
  {
    id: 'bajitos',
    name: 'Bajitos',
    faction: 'cholo',
    color: 0xd4a945, // Tecate-can gold (lowrider chrome+paint)
    verb: 'BRINCO',
    fantasy: 'You turn a 2/40 wall into a 43/43 monster. Big dumb stats, on purpose.',
    description: 'Hydraulics and stat tricks. The hopper redefines what wins.',
    breakpoints: [
      {
        threshold: 2,
        description: 'Start of combat: Every Bajito gains +3 to its higher stat.',
        statManipulation: 'boost_higher',
      },
      {
        threshold: 4,
        description: "Start of combat: Swap Bajitos' Atk/HP if beneficial, then +3/+3.",
        statManipulation: 'swap_if_beneficial',
        manipulationBonus: { attack: 3, health: 3 },
      },
      {
        threshold: 6,
        description:
          "Start of combat: Set every Bajito's Attack equal to its Health, then +3/+3.",
        statManipulation: 'set_attack_to_health',
        manipulationBonus: { attack: 3, health: 3 },
      },
    ],
  },
];

/** Look up a tribe definition by ID */
export function getTribeDefinition(id: TribeId): TribeDefinition | undefined {
  return TRIBE_DEFINITIONS.find(t => t.id === id);
}

/**
 * The highest breakpoint whose threshold `count` actually meets, or null
 * below the first one.
 *
 * Breakpoints are HIGHEST-ONLY, not cumulative: reaching 4 replaces the 2
 * payoff rather than stacking with it. Both engine phases rely on this —
 * synergyAuras (combat) and tribeEndOfTurn (tavern) each had their own
 * private copy of this function; they now share this one so the UI cannot
 * describe a payoff the engine wouldn't actually apply.
 */
export function getActiveBreakpoint(
  tribe: TribeDefinition,
  count: number,
): TribeSynergyBreakpoint | null {
  let best: TribeSynergyBreakpoint | null = null;
  for (const bp of tribe.breakpoints) {
    if (count >= bp.threshold) best = bp;
  }
  return best;
}

/**
 * The next breakpoint the board has NOT yet reached, or null once the top
 * one is met. Drives the "1 more → 4" progress hint.
 */
export function getNextBreakpoint(
  tribe: TribeDefinition,
  count: number,
): TribeSynergyBreakpoint | null {
  for (const bp of tribe.breakpoints) {
    if (count < bp.threshold) return bp;
  }
  return null;
}

/** Get all tribes belonging to a faction */
export function getTribesByFaction(faction: 'emo' | 'cholo'): TribeDefinition[] {
  return TRIBE_DEFINITIONS.filter(t => t.faction === faction);
}

/** Map of tribe ID → hex color for UI use */
export const TRIBE_COLORS: Record<TribeId, number> = Object.fromEntries(
  TRIBE_DEFINITIONS.map(t => [t.id, t.color])
) as Record<TribeId, number>;
